import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Linkedin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { profile } from "../../data/resume";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  { icon: Mail, label: "Email", value: profile.email, href: `mailto:${profile.email}`, testid: "contact-email" },
  { icon: Phone, label: "Phone", value: profile.phone, href: profile.phoneHref, testid: "contact-phone" },
  { icon: Linkedin, label: "LinkedIn", value: profile.linkedin, href: profile.linkedinHref, testid: "contact-linkedin" },
  { icon: MapPin, label: "Location", value: profile.location, href: null, testid: "contact-location" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been sent.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-paper border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: intro + info */}
          <div className="lg:col-span-5 reveal">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">
              Contact
            </p>
            <h2 className="font-serif font-semibold text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Let's start a conversation.
            </h2>
            <p className="mt-6 text-lg text-slatey leading-relaxed">
              Open to senior finance, FP&A, and budget leadership opportunities.
              Reach out directly or send a message — I'll respond promptly.
            </p>

            <div className="mt-10 space-y-px bg-border border border-border">
              {contactInfo.map((c) => {
                const Inner = (
                  <div className="flex items-center gap-4 bg-white p-5 hover:bg-paper transition-colors">
                    <span className="flex items-center justify-center h-10 w-10 border border-border text-brass shrink-0">
                      <c.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-slate-400">{c.label}</div>
                      <div className="text-ink font-medium truncate">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid} className="block">
                    {Inner}
                  </a>
                ) : (
                  <div key={c.label} data-testid={c.testid}>{Inner}</div>
                );
              })}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "0.1s" }}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-border p-7 md:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-ink">Name</Label>
                  <Input
                    id="name" name="name" value={form.name} onChange={onChange}
                    data-testid="contact-input-name"
                    placeholder="Your full name"
                    className="mt-2 rounded-none border-border focus-visible:ring-brass h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-ink">Email</Label>
                  <Input
                    id="email" name="email" type="email" value={form.email} onChange={onChange}
                    data-testid="contact-input-email"
                    placeholder="you@company.com"
                    className="mt-2 rounded-none border-border focus-visible:ring-brass h-12"
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="subject" className="text-ink">Subject</Label>
                <Input
                  id="subject" name="subject" value={form.subject} onChange={onChange}
                  data-testid="contact-input-subject"
                  placeholder="What's this about?"
                  className="mt-2 rounded-none border-border focus-visible:ring-brass h-12"
                />
              </div>

              <div className="mt-6">
                <Label htmlFor="message" className="text-ink">Message</Label>
                <Textarea
                  id="message" name="message" value={form.message} onChange={onChange}
                  data-testid="contact-input-message"
                  placeholder="Tell me a little about the opportunity or question..."
                  rows={6}
                  className="mt-2 rounded-none border-border focus-visible:ring-brass resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-ink text-white px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send message <Send className="h-4 w-4" strokeWidth={1.5} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
